import { CommonModule } from '@angular/common';
import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { ButtonComponent, ButtonTriggerEvent } from '../button/button.component';
import { AuthService, TokenInfo } from '../../services/auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { getRouteUrl, routeConfigs } from '../../constants/route.config';
import jwt_decode from 'jwt-decode';
import { catchError, finalize, of } from 'rxjs';
import { PendoService } from 'src/app/modules/shared/services/pendo.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'ig-login',
  standalone: true,
  imports: [CommonModule, SharedModule, FormsModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent {
  credentials = { userName: '', password: '' };
  form: FormGroup;
  accountStatus: string | null = null;


  @ViewChild('loginBtn', { static: true }) loginBtn: ButtonComponent;

  constructor(
    private _fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private pendoService: PendoService
  ) {
    this.form = this._fb.group({
      userName: [null, [Validators.required]],
      password: [null, [Validators.required]],
    });
  }

  // Update login method
  login(event?: ButtonTriggerEvent) {
    this.accountStatus = null;
    // const returnUrl = this.route.snapshot.queryParams['returnUrl'];

    // trigger when enter key pressed
    !event && this.loginBtn.load();

    this.authService
      .login(this.credentials)
      .pipe(
        catchError((error) => {
          console.error('Login failed:', error);
          this.accountStatus = this.getLoginErrorMessage(error);
          return of(null);
        }),
        finalize(() => {
          event?.complete();
          !event && this.loginBtn.complete();
        })
      )
      .subscribe({
        next: (token) => {
          if (!token) {
            return;
          }

          // Update the usage in login method
          let tokenInfo = jwt_decode<TokenInfo>(token);
          console.log(tokenInfo);

          if (tokenInfo.status !== 'live') {
            this.accountStatus = 'Your account is disabled, please contact administrator.';
            return;
          }

          if (tokenInfo.isAdministrator) {
            if (tokenInfo.role === 'ig-admin') {
              this.router.navigateByUrl(getRouteUrl(routeConfigs.admin.accounts));
            }
            if (tokenInfo.role === 'ig-event-puller') {
              this.router.navigateByUrl(getRouteUrl(routeConfigs.admin));
            }
          } else {
            this.pendoService.initialize(
              tokenInfo.userName, {
              role: tokenInfo.role,
              hubName: tokenInfo.hubName || tokenInfo.userName
            });

            // defaults to true if not set
            const isEventEnabled =
              tokenInfo.toggle.eventByGeolocation !== false ||
              tokenInfo.toggle.manualEvents !== false;

            const isPlaceEnabled =
              tokenInfo.toggle.placeCalendar !== false;

            const isJobBoardEnabled =
              tokenInfo.toggle.jobBoard !== false;

            let routeUrl = { path: '' };
            if (isEventEnabled) {
              routeUrl = routeConfigs.dashboard.eventCalendar;
            } else if (isPlaceEnabled) {
              routeUrl = routeConfigs.dashboard.placeCalendar;
            } else if (isJobBoardEnabled) {
              routeUrl = routeConfigs.dashboard.jobBoard;
            }             

            //TODO: need to handle disable event, place, and job board case

            // for account users (not login with account name, but use user name under an account)
            // need to redirect to tokenInfo.hubName instead of tokenInfo.userName
            this.router.navigateByUrl(
              getRouteUrl(routeUrl).replace(':hubName', tokenInfo.hubName || tokenInfo.userName)
            );
          }
        },
        error: (error) => {
          console.error('Login failed:', error);
          event.complete();
          // Handle error display here
        },
      });
  }

  private getLoginErrorMessage(error: HttpErrorResponse): string {
    if (error.status !== 429) {
      return 'Invalid credentials';
    }

    const retryAfterSeconds = Number(error.error?.retryAfterSeconds);
    if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
      return 'Too many login attempts. Please try again later.';
    }

    const retryAfterMinutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
    const unit = retryAfterMinutes === 1 ? 'minute' : 'minutes';
    return `Too many login attempts. Please try again in ${retryAfterMinutes} ${unit}.`;
  }
}
